import type { Breadcrumb, ErrorEvent } from '@sentry/react';

// Connect handles a lot of requester/provider PII (names, emails, phones,
// addresses). Anything leaving the browser is scrubbed defensively here.

const filtered = '[Filtered]';

const emailPattern = /[\w.%+-]+@[\w.-]+\.[a-z]{2,}/gi;
const phonePattern = /(?:\+|\b00)?\d(?:[\s./()-]?\d){8,}/g;

const urlKeys = ['url', 'from', 'to'] as const;

export function scrubText(value: string): string {
  return value.replace(emailPattern, filtered).replace(phonePattern, filtered);
}

// Query strings and hashes can carry search terms, filters or OAuth codes.
export function scrubUrl(value: string): string {
  return scrubText(value.split(/[?#]/)[0]);
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  // Console arguments may contain whole API payloads.
  if (breadcrumb.category === 'console') {
    return { ...breadcrumb, data: undefined, message: filtered };
  }

  const data = breadcrumb.data ? { ...breadcrumb.data } : undefined;
  if (data) {
    for (const key of urlKeys) {
      if (typeof data[key] === 'string') {
        data[key] = scrubUrl(data[key]);
      }
    }
  }

  return {
    ...breadcrumb,
    data,
    message: breadcrumb.message ? scrubText(breadcrumb.message) : undefined,
  };
}

function scrubRequest(request: ErrorEvent['request']): ErrorEvent['request'] {
  if (!request) {
    return undefined;
  }

  const userAgent = request.headers?.['User-Agent'];
  const referer = request.headers?.Referer;

  return {
    headers: {
      ...(userAgent && { 'User-Agent': userAgent }),
      ...(referer && { Referer: scrubUrl(referer) }),
    },
    method: request.method,
    url: request.url ? scrubUrl(request.url) : undefined,
  };
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
  return {
    ...event,
    breadcrumbs: event.breadcrumbs?.map(scrubBreadcrumb),
    exception: event.exception && {
      ...event.exception,
      values: event.exception.values?.map(exception => ({
        ...exception,
        value: exception.value ? scrubText(exception.value) : undefined,
      })),
    },
    message: event.message ? scrubText(event.message) : undefined,
    request: scrubRequest(event.request),
    user: event.user?.id ? { id: event.user.id } : undefined,
  };
}
